import { useState, useEffect, useMemo } from "react";
import { Layout } from "react-grid-layout";
import {
  MetricModel,
  DashboardModel,
  LayoutItem,
  ontologyUpdateDashboard,
} from "@wayak/ai/sdk";

// Type for the return values of the hook
interface UseDashboardDataReturn {
  dashboard: DashboardModel | undefined;
  metrics: MetricModel[];
  dashboardMetrics: Array<{ metric: MetricModel; layoutItem: LayoutItem }>;
  availableMetrics: MetricModel[];
  currentLayout: Layout[];
  editedName: string;
  editedDescription: string;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  setEditedName: (value: string) => void;
  setEditedDescription: (value: string) => void;
  handleLayoutChange: (newLayout: Layout[]) => void;
  handleSaveLayout: () => Promise<void>;
  handleAddMetric: (metric: MetricModel, onComplete?: () => void) => void;
  handleRemoveMetric: (metricId: string) => void;
  getMetricTitle: (metric: MetricModel) => string;
  getMetricDescription: (metric: MetricModel) => string;
}

interface UseDashboardDataProps {
  initialDashboard: DashboardModel | undefined;
  metricsCatalog: MetricModel[];
}

export function useDashboardData({
  initialDashboard,
  metricsCatalog,
}: UseDashboardDataProps): UseDashboardDataReturn {
  // Create a state for the dashboard to properly track updates
  const [dashboard, setDashboard] = useState<DashboardModel | undefined>(
    initialDashboard
  );

  // Update dashboard state when initialDashboard changes
  useEffect(() => {
    setDashboard(initialDashboard);
  }, [initialDashboard]);

  // States for editing
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentLayout, setCurrentLayout] = useState<Layout[]>([]);
  const [editedName, setEditedName] = useState<string>("");
  const [editedDescription, setEditedDescription] = useState<string>("");

  // Initialize dashboard state
  useEffect(() => {
    if (dashboard) {
      setEditedName(dashboard.name);
      setEditedDescription(dashboard.description || "");

      // Initialize the currentLayout based on dashboard.layout
      if (dashboard.layout && dashboard.layout.length > 0) {
        const layout = dashboard.layout
          .filter((item) => item.metricId != null)
          .map((item) => ({
            i: item.metricId as string,
            x: item.position?.x ?? 0,
            y: item.position?.y ?? 0,
            w: item.position?.w ?? 2,
            h: item.position?.h ?? 2,
          }));

        setCurrentLayout(layout);
      } else {
        setCurrentLayout([]);
      }
    }
  }, [dashboard]);

  // Memoize the layout
  const layout = useMemo(() => {
    if (!dashboard) return [];

    if (currentLayout.length > 0) {
      return currentLayout;
    }

    if (!dashboard.layout) {
      return [];
    }

    return dashboard.layout
      .filter((item) => item.metricId != null)
      .map((item) => ({
        i: item.metricId,
        x: item.position?.x ?? 0,
        y: item.position?.y ?? 0,
        w: item.position?.w ?? 2,
        h: item.position?.h ?? 2,
      }));
  }, [currentLayout, dashboard]);

  // Memoize dashboardMetrics
  const dashboardMetrics = useMemo(() => {
    if (!dashboard || !dashboard.layout) return [];

    return dashboard.layout
      .filter(
        (item) =>
          item.metricId && metricsCatalog.find((m) => m.id === item.metricId)
      )
      .map((item) => {
        const metricId = item.metricId as string;
        return {
          metric: metricsCatalog.find((m) => m.id === metricId) as MetricModel,
          layoutItem: item,
        };
      });
  }, [dashboard, metricsCatalog]);

  // Memoize availableMetrics
  const availableMetrics = useMemo(() => {
    if (!dashboard) return [];

    // Create a Set of metric IDs that are already on the dashboard
    const dashboardMetricIds = new Set<string>();

    dashboardMetrics.forEach((dm) => {
      if (dm.metric && typeof dm.metric.id === "string") {
        dashboardMetricIds.add(dm.metric.id);
      }
    });

    // Only include metrics that aren't already on the dashboard
    return metricsCatalog.filter(
      (metric) =>
        typeof metric.id === "string" && !dashboardMetricIds.has(metric.id)
    );
  }, [metricsCatalog, dashboardMetrics, dashboard]);

  // Debounce layout changes
  const handleLayoutChange = (newLayout: Layout[]): void => {
    // Only update if actually changed to avoid unnecessary rerenders
    if (JSON.stringify(newLayout) !== JSON.stringify(currentLayout)) {
      setCurrentLayout(newLayout);
    }
  };

  const handleSaveLayout = async (): Promise<void> => {
    if (!dashboard) return;

    // Convert the layout back to the dashboard.layout format
    const newDashboardLayout: LayoutItem[] = currentLayout.map((item) => ({
      viewId: item.i,
      metricId: item.i,
      position: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      },
    }));

    // Update the dashboard state with the new data
    const updatedDashboard = {
      ...dashboard,
      name: editedName,
      description: editedDescription,
      layout: newDashboardLayout,
    };

    setDashboard(updatedDashboard);

    console.log("Saving dashboard:", updatedDashboard);

    if (dashboard.id) {
      await ontologyUpdateDashboard({
        path: { dashboard_id: dashboard.id },
        body: {
          name: editedName,
          description: editedDescription,
          layout: newDashboardLayout,
        },
      });
    }

    setIsEditing(false);
  };

  const handleAddMetric = (
    metric: MetricModel,
    onComplete?: () => void
  ): void => {
    if (!metric || !metric.id) return;

    // Create a new layout item for this metric
    const newLayoutItem: Layout = {
      i: metric.id,
      x: 0,
      y: 0,
      w: 2,
      h: 2,
    };

    // Create proper format for dashboard layout
    const newDashboardMetric: LayoutItem = {
      viewId: metric.id,
      metricId: metric.id,
      position: {
        x: 0,
        y: 0,
        w: 2,
        h: 2,
      },
    };

    // Add to the current layout for grid component
    setCurrentLayout((prevLayout) => [...prevLayout, newLayoutItem]);

    // Add to the dashboard model for rendering metrics
    if (dashboard) {
      const newLayout = dashboard.layout
        ? [...dashboard.layout, newDashboardMetric]
        : [newDashboardMetric];

      setDashboard({
        ...dashboard,
        layout: newLayout,
      });
    }

    if (onComplete) {
      onComplete();
    }
  };

  const handleRemoveMetric = (metricId: string): void => {
    if (!dashboard || !metricId) return;

    console.log("Removing metric:", metricId);

    // Remove from the current layout (used by grid)
    setCurrentLayout((prevLayout) =>
      prevLayout.filter((item) => item.i !== metricId)
    );

    // Remove from the dashboard model
    if (dashboard.layout) {
      // Create a new array instead of mutating the existing one
      const newLayout = dashboard.layout.filter(
        (item) => item.metricId !== metricId
      );

      // Update the dashboard state with the new layout
      setDashboard({
        ...dashboard,
        layout: newLayout,
      });
    }
  };

  // Helper functions to safely access metric properties
  const getMetricTitle = (metric: MetricModel): string => {
    return metric.title || "Untitled Metric";
  };

  const getMetricDescription = (metric: MetricModel): string => {
    return metric.description || "";
  };

  return {
    dashboard,
    metrics: metricsCatalog,
    dashboardMetrics,
    availableMetrics,
    currentLayout: layout,
    editedName,
    editedDescription,
    isEditing,
    setIsEditing,
    setEditedName,
    setEditedDescription,
    handleLayoutChange,
    handleSaveLayout,
    handleAddMetric,
    handleRemoveMetric,
    getMetricTitle,
    getMetricDescription,
  };
}
